import React from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { SlackIcon, DiscordIcon } from '../../../components/shared/SocialIcons';
import ToggleSwitch from '../../../components/shared/ToggleSwitch';
import {
  Calendar,
  TrendingUp,
  Eye,
  Send,
  Loader2,
  Check,
  Mail,
  History
} from 'lucide-react';

const MonitoringReports = ({
  reportConfig,
  reportHistory,
  previewReport,
  savingReportConfig,
  generatingReport,
  onSaveReportConfig,
  onGenerateReport,
  onPreviewReport,
  onClosePreview,
  updateReportConfig
}) => {
  if (!reportConfig) return null;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Scheduled Reports</h3>
            <p className="text-sm text-gray-600">Configure daily and weekly system health reports</p>
          </div>
          <Button
            onClick={onSaveReportConfig}
            disabled={savingReportConfig}
            className="bg-pink-600 hover:bg-pink-700"
          >
            {savingReportConfig ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        <div className="space-y-8">
          {/* Daily Reports */}
          <div className="p-6 border rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Daily Report</h4>
                  <p className="text-sm text-gray-600">Summary of the last 24 hours</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPreviewReport('daily')}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onGenerateReport('daily')}
                  disabled={generatingReport === 'daily'}
                >
                  {generatingReport === 'daily' ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-1" />
                  )}
                  Send Now
                </Button>
                <ToggleSwitch
                  enabled={reportConfig.daily_enabled}
                  onToggle={() => updateReportConfig('daily_enabled', !reportConfig.daily_enabled)}
                  activeColor="bg-blue-600"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Send at</label>
                <Input
                  type="time"
                  value={reportConfig.daily_time || '09:00'}
                  onChange={(e) => updateReportConfig('daily_time', e.target.value)}
                  className="w-32 mt-1"
                />
              </div>
            </div>
          </div>

          {/* Weekly Reports */}
          <div className="p-6 border rounded-lg bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Weekly Report</h4>
                  <p className="text-sm text-gray-600">Summary of the last 7 days with trends</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPreviewReport('weekly')}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onGenerateReport('weekly')}
                  disabled={generatingReport === 'weekly'}
                >
                  {generatingReport === 'weekly' ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-1" />
                  )}
                  Send Now
                </Button>
                <ToggleSwitch
                  enabled={reportConfig.weekly_enabled}
                  onToggle={() => updateReportConfig('weekly_enabled', !reportConfig.weekly_enabled)}
                  activeColor="bg-purple-600"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Day</label>
                <select
                  value={reportConfig.weekly_day || 'monday'}
                  onChange={(e) => updateReportConfig('weekly_day', e.target.value)}
                  className="w-32 mt-1 px-3 py-2 border rounded-md text-sm"
                >
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <option key={day} value={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Time</label>
                <Input
                  type="time"
                  value={reportConfig.weekly_time || '09:00'}
                  onChange={(e) => updateReportConfig('weekly_time', e.target.value)}
                  className="w-32 mt-1"
                />
              </div>
            </div>
          </div>

          {/* Report Content Settings */}
          <Card className="p-6 bg-gray-50">
            <h4 className="font-semibold mb-4">Report Content</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Alerts Summary</p>
                  <p className="text-sm text-gray-600">Include alert counts and types</p>
                </div>
                <ToggleSwitch
                  enabled={reportConfig.include_alerts_summary}
                  onToggle={() => updateReportConfig('include_alerts_summary', !reportConfig.include_alerts_summary)}
                  activeColor="bg-green-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Performance Metrics</p>
                  <p className="text-sm text-gray-600">Include CPU, memory, disk, and cache stats</p>
                </div>
                <ToggleSwitch
                  enabled={reportConfig.include_performance_metrics}
                  onToggle={() => updateReportConfig('include_performance_metrics', !reportConfig.include_performance_metrics)}
                  activeColor="bg-green-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Business Metrics</p>
                  <p className="text-sm text-gray-600">Include rides, revenue, and user stats</p>
                </div>
                <ToggleSwitch
                  enabled={reportConfig.include_business_metrics}
                  onToggle={() => updateReportConfig('include_business_metrics', !reportConfig.include_business_metrics)}
                  activeColor="bg-green-500"
                />
              </div>
            </div>
          </Card>

          {/* Delivery Channels */}
          <Card className="p-6 bg-gray-50">
            <h4 className="font-semibold mb-4">Delivery Channels</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-gray-600">Send to all admin emails</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={reportConfig.send_to_email}
                  onToggle={() => updateReportConfig('send_to_email', !reportConfig.send_to_email)}
                  activeColor="bg-green-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SlackIcon />
                  <div>
                    <p className="font-medium">Slack</p>
                    <p className="text-sm text-gray-600">Send to configured Slack channel</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={reportConfig.send_to_slack}
                  onToggle={() => updateReportConfig('send_to_slack', !reportConfig.send_to_slack)}
                  activeColor="bg-purple-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DiscordIcon />
                  <div>
                    <p className="font-medium">Discord</p>
                    <p className="text-sm text-gray-600">Send to configured Discord channel</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={reportConfig.send_to_discord}
                  onToggle={() => updateReportConfig('send_to_discord', !reportConfig.send_to_discord)}
                  activeColor="bg-indigo-500"
                />
              </div>
            </div>
          </Card>

          {/* Report History */}
          {reportHistory.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <History className="h-5 w-5 text-gray-600" />
                <h4 className="font-semibold">Recent Reports</h4>
              </div>
              <div className="space-y-2">
                {reportHistory.map((report, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge className={report.type === 'daily' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                        {report.type}
                      </Badge>
                      <span className="text-sm">{report.period?.label}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(report.generated_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </Card>

      {/* Report Preview Modal */}
      {previewReport && (
        <Card className="p-6 border-2 border-pink-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Report Preview</h3>
            <Button variant="outline" size="sm" onClick={onClosePreview}>
              Close
            </Button>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="text-xs overflow-auto max-h-96">
              {JSON.stringify(previewReport, null, 2)}
            </pre>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MonitoringReports;
